/**
 * A fase do site num instante (ORD-F-054; ORD-F2-032/036): a página inicial até 24/10 23h59:59, a pré-apuração de 25/10
 * 00h00 às 16h59:59, a apuração a partir das 17h00 e, depois dela, a abertura pós-apuração (`after`, pedido de 26/10) a
 * partir de um instante configurável (`after_at` do #app-config; padrão 26/10 06h00), sempre horário de Brasília (UTC−3
 * fixo). Arquivo PURO, sem sinais nem store: o app usa esta função (src/app/sitePhase.ts) e o esboço do boot a embute em
 * ES5 (scripts/vite/inline-boot.ts), para os dois decidirem a fase do mesmo jeito (condição do SINAL-F-012). Mesmo cálculo
 * de `epochMs` e `T_OPEN` (tests/unit/app/phaseClock.test.ts confere a paridade).
 */
export type SitePhase = 'home' | 'pre' | 'live' | 'after';

/** 17h00 em minutos desde 00h00 (= `T_OPEN` de src/model/time.ts). */
const OPEN_MIN = 1020;
/** Brasília = UTC−3 fixo (= `BRT_OFFSET_MIN` de src/format/time.ts). */
const BRT_OFFSET_MS = -180 * 60_000;
/** Data padrão quando `electionDate` é ilegível (= `ELECTION_DATE` de src/format/time.ts). */
const DEFAULT_DATE = '2026-10-25';
/** Padrão da abertura pós-apuração: 06h00 do dia seguinte à eleição (30 h depois de 00h00 do dia da eleição). */
export const AFTER_DEFAULT_MIN = 30 * 60;
/** `after_at`: "AAAA-MM-DDTHH:MM[:SS]" com "Z" ou "±HH:MM" (sem `Date.parse`, que varia entre navegadores antigos). */
const AFTER_AT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(Z|[+-]\d{2}:\d{2})$/;

/** 00h00 de Brasília de "AAAA-MM-DD" (aceita ISO completo); ilegível, a data padrão. */
export function electionDayMs(electionDate: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(electionDate);
  const ok = m !== null && Number(m[2]) >= 1 && Number(m[2]) <= 12 && Number(m[3]) >= 1 && Number(m[3]) <= 31;
  if (!ok) return electionDayMs(DEFAULT_DATE);
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - BRT_OFFSET_MS;
}

/** O instante (ms) de `after_at`, ou null quando ausente ou ilegível. */
export function parseAfterAt(afterAt: string | null | undefined): number | null {
  const m = typeof afterAt === 'string' ? AFTER_AT_RE.exec(afterAt) : null;
  if (m === null) return null;
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const h = Number(m[4]);
  const mi = Number(m[5]);
  const s = m[6] ? Number(m[6]) : 0;
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || s > 59) return null;
  const tz = m[7] as string;
  let offsetMin = 0;
  if (tz !== 'Z') {
    const oh = Number(tz.slice(1, 3));
    const om = Number(tz.slice(4, 6));
    if (oh > 14 || om > 59) return null;
    offsetMin = (tz.charAt(0) === '-' ? -1 : 1) * (oh * 60 + om);
  }
  return Date.UTC(Number(m[1]), mo - 1, d, h, mi, s) - offsetMin * 60_000;
}

/**
 * Início da abertura pós-apuração (ms): `after_at` legível ou, sem ele, 06h00 do dia seguinte. Nunca antes da abertura da
 * apuração (17h00 do dia da eleição): um `after_at` mais cedo vale a partir das 17h00, e a apuração não some antes de abrir.
 */
export function afterStartMs(electionDate: string, afterAt?: string | null): number {
  const open = electionDayMs(electionDate) + OPEN_MIN * 60_000;
  const at = parseAfterAt(afterAt);
  const start = at === null ? electionDayMs(electionDate) + AFTER_DEFAULT_MIN * 60_000 : at;
  return start > open ? start : open;
}

/** A fase em `nowMs` (ms de época), para a eleição de `electionDate` e a abertura pós-apuração de `afterAt`. */
export function sitePhaseAt(nowMs: number, electionDate: string, afterAt?: string | null): SitePhase {
  const day = electionDayMs(electionDate);
  if (nowMs < day) return 'home';
  if (nowMs < day + OPEN_MIN * 60_000) return 'pre';
  if (nowMs < afterStartMs(electionDate, afterAt)) return 'live';
  return 'after';
}

/** A página inicial (o pedaço `homeEntry`) está na tela nesta fase: antes da eleição e na abertura pós-apuração. */
export function isHomeEntryPhase(phase: SitePhase): boolean {
  return phase === 'home' || phase === 'after';
}

/** Endereço de rota (`#/…` com algo depois da barra): na fase `after`, com acesso, a apuração abre direto. */
export function hasRouteHash(hash: string): boolean {
  return /^#\/[^?#\s]/.test(hash);
}

/**
 * Na fase `after`, a entrada abre a apuração direto (sem a abertura): endereço de rota e acesso ao app (sem a porta, ou
 * com sessão guardada). O app decide por `siteAccess` (main.tsx → `seedAppOpen`); o boot, por `prefetched` = a pré-busca
 * do snapshot saiu (src/boot/prefetch.ts: só no modo ao vivo, fora do pacote só da capa e, com a porta, só com sessão
 * guardada; a mesma regra). Assim o esboço e a pré-carga do <head> desenham a tela que o app vai montar.
 */
export function afterOpensAppOnEntry(hash: string, hasAccess: boolean): boolean {
  return hasAccess && hasRouteHash(hash);
}
